
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MetalPrice } from "@/lib/metal-price";
import { cn } from "@/lib/utils";
import { ArrowDown, ArrowUp, Clock } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { MetalPurityModal } from "./MetalPurityModal";
import { useLongPress } from "@/hooks/useLongPress";
import { formatPurity } from "@/lib/formatPurity";
import { getPurityMultiplier, Purity } from "@/lib/pricing";

interface PriceCardProps {
  metalPrice: MetalPrice | null;
  isLoading?: boolean;
  className?: string;
  metal: 'gold' | 'silver';
  purity: string;
  unit?: string;
  formattedPrice: string;
  onPurityChange: (purity: string) => void;
  onUnitChange?: (unit: string) => void;
  onClick?: () => void;
}

export function PriceCard({ 
  metalPrice, 
  isLoading = false, 
  className,
  metal,
  purity,
  unit,
  formattedPrice,
  onPurityChange,
  onUnitChange,
  onClick
}: PriceCardProps) {
  const { setMetalTheme } = useTheme();
  const [isPurityModalOpen, setIsPurityModalOpen] = useState(false);
  
  const longPressProps = useLongPress(() => {
    setIsPurityModalOpen(true);
  }, 500);
  
  const metalNames = {
    gold: "Oro",
    silver: "Argento"
  };
  
  const handleCardClick = () => {
    if (onClick) onClick();
    setMetalTheme(metal as "gold" | "silver");
  };

  const formattedPurity = formatPurity(metal, purity);

  const cardStyle = metal === 'gold' 
    ? 'gold-theme border-gold/50 bg-gold/5 hover:border-gold' 
    : 'silver-theme border-silver/50 bg-silver/5 hover:border-silver';

  const accentStyle = metal === 'gold' 
    ? 'bg-gold' 
    : 'bg-silver';

  const textStyle = metal === 'gold' 
    ? 'text-gold-dark dark:text-gold-light' 
    : 'text-silver-dark dark:text-silver-light';

  let adjustedChange = 0;
  let adjustedChangePercentage = 0;
  let formattedDate = '';
  let changeFormatted = '';
  let isPositive = false;

  if (metalPrice && !isLoading) {
    const { change, changePercentage, timestamp } = metalPrice;
    formattedDate = new Date(timestamp).toLocaleString('it-IT');
    
    const purityMultiplier = getPurityMultiplier(
      metal === 'gold' ? 'ORO' : 'ARGENTO',
      purity as Purity
    );
    
    adjustedChange = change * purityMultiplier;
    isPositive = adjustedChange >= 0;
    
    adjustedChangePercentage = changePercentage;
    
    changeFormatted = adjustedChange.toLocaleString('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  if (isLoading || !metalPrice) {
    return (
      <Card className={cn("w-full", className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-medium capitalize animate-pulse bg-muted h-7 w-24 rounded"></CardTitle>
          <CardDescription className="animate-pulse bg-muted h-4 w-40 rounded"></CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold animate-pulse bg-muted h-9 w-32 rounded"></div>
          <div className="flex items-center mt-2">
            <div className="animate-pulse bg-muted h-5 w-16 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        className={cn(
          "w-full transition-all duration-300 hover:shadow-md cursor-pointer overflow-hidden border-2 relative",
          cardStyle,
          className
        )}
        onClick={handleCardClick}
        {...longPressProps}
      >
        <div className={cn("absolute top-0 left-0 w-full h-1", accentStyle)}></div>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle 
              className={cn(
                "text-2xl font-bold capitalize flex items-center",
                textStyle
              )}
            >
              {metalNames[metal]}
              <Badge 
                variant="outline" 
                className={cn("ml-2", metal === 'gold' ? "border-gold" : "border-silver")}
              >
                {formattedPurity}
              </Badge>
            </CardTitle>
            
            {metal === 'silver' && unit && (
              <Badge variant="secondary">€/{unit}</Badge>
            )}
          </div>
          <CardDescription className="flex items-center gap-1 text-sm text-muted-foreground">
            <Clock className="h-3 w-3" /> {formattedDate}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold mb-2">{formattedPrice}</div>
          <div className={cn(
            "flex items-center text-sm px-2 py-1 rounded-full w-fit",
            isPositive ? "bg-green-600/20 text-green-600" : "bg-red-600/20 text-red-600"
          )}>
            {isPositive ? <ArrowUp className="h-4 w-4 mr-1" /> : <ArrowDown className="h-4 w-4 mr-1" />}
            <span>{changeFormatted} ({adjustedChangePercentage.toFixed(2)}%)</span>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Premi a lungo per cambiare purezza
          </div>
        </CardContent>
      </Card>
      
      <MetalPurityModal
        isOpen={isPurityModalOpen}
        onClose={() => setIsPurityModalOpen(false)}
        metal={metal}
        currentPurity={purity}
        currentUnit={unit}
        onPurityChange={onPurityChange}
        onUnitChange={onUnitChange}
      />
    </>
  );
}
